import { useEffect } from "react";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import {
    ENFORCE_LOGIN_HINT_EXPIRATION_PROPERTY_TYPE_ID,
    OIDC_ID_TOKEN_USER_ATTRIBUTES_PROPERTY_TYPE_ID,
    OIDC_LOGINHINT_EXPIRATION_PROPERTY_TYPE_ID,
    OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID,
    OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID,
    OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID
} from "@constants/oidc-login-hint";
import { LoginHintSecurityMethodTypes } from "@enums/login-hint-security-methode-types";
import { PageLevelTypes } from "@enums/page-level-types";
import { GeneralAdvancedValidationErrors } from "@idw-types/common/validation/common/general-advanced-validation-errors";
import { AxnDocumentationLink } from "@idw-types/data/reference-data/axn/axn-documentation-link";
import { FormattedWorkflowDetailsConfiguration } from "@idw-types/data/workflow-details-configuration";
import { LoginHintContainer } from "@containers/common/properties-sections/login-hint-container/login-hint-container";
import { getPropertyValuesPassedToService } from "@utils/applied-profile-util";

type WorkflowLoginHintContainerProps = {
    isDisabled: boolean;
    validationErrors: GeneralAdvancedValidationErrors;
    documentationLinks: Array<AxnDocumentationLink>;
    workflowDetailsConfigurationState: FormattedWorkflowDetailsConfiguration;
    updateProperties: (properties: Array<{
        propertyTypeId: string;
        newValue: string;
        added: boolean;
    }>) => void;
};

export const WorkflowLoginHintContainer = (props: WorkflowLoginHintContainerProps) => {
    const {
        isDisabled,
        validationErrors,
        documentationLinks,
        workflowDetailsConfigurationState,
        updateProperties
    } = props;

    useEffect(() => {
        const oidcIdTokenUserAttributesPropertyValue = getPropertyValuesPassedToService(
            workflowDetailsConfigurationState.appliedProfiles,
            OIDC_ID_TOKEN_USER_ATTRIBUTES_PROPERTY_TYPE_ID
        );
        if (!isDisabled) {
            const propertiesToUpdate: Array<{ propertyTypeId: string; newValue: string; added: boolean; }> = [];
            if (isStringTrue(oidcIdTokenUserAttributesPropertyValue)) {
                //if jwt is disabled (credentials only), set its property value to false
                if (getPropertyValuesPassedToService(workflowDetailsConfigurationState.appliedProfiles, OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID) === null) {
                    propertiesToUpdate.push({ propertyTypeId: OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID, newValue: "false", added: true });
                }
                //if login hint is disabled, set its property value to false
            } else if (oidcIdTokenUserAttributesPropertyValue === null) {
                propertiesToUpdate.push({ propertyTypeId: OIDC_ID_TOKEN_USER_ATTRIBUTES_PROPERTY_TYPE_ID, newValue: "false", added: true });
            }
            updateProperties(propertiesToUpdate);
        }
    }, []);

    const onChangeEnableLoginHint = (newValue: string) => {
        if (isStringTrue(newValue)) {
            updateProperties([
                { propertyTypeId: OIDC_ID_TOKEN_USER_ATTRIBUTES_PROPERTY_TYPE_ID, newValue: "true", added: true },
                { propertyTypeId: OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID, newValue: null, added: false },
            ]);
        } else {
            updateProperties([
                { propertyTypeId: OIDC_ID_TOKEN_USER_ATTRIBUTES_PROPERTY_TYPE_ID, newValue: "false", added: true },
                { propertyTypeId: OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID, newValue: null, added: false },
            ]);
        }
    };

    const onChangeLoginHintSecurityMethod = (newValue: LoginHintSecurityMethodTypes) => {
        const propertiesToUpdate: Array<{ propertyTypeId: string; newValue: string; added: boolean; }> = [];
        if (newValue === LoginHintSecurityMethodTypes.PLAIN_JSON) {
            propertiesToUpdate.push(...[
                { propertyTypeId: OIDC_ID_TOKEN_USER_ATTRIBUTES_PROPERTY_TYPE_ID, newValue: "true", added: true },
                { propertyTypeId: OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID, newValue: "false", added: true },
                { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: OIDC_LOGINHINT_EXPIRATION_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: ENFORCE_LOGIN_HINT_EXPIRATION_PROPERTY_TYPE_ID, newValue: null, added: false }

            ]);
        } else if (newValue === LoginHintSecurityMethodTypes.SIGNED_JWT) {
            propertiesToUpdate.push(...[
                { propertyTypeId: OIDC_ID_TOKEN_USER_ATTRIBUTES_PROPERTY_TYPE_ID, newValue: "true", added: true },
                { propertyTypeId: OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID, newValue: "true", added: true },
                { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID, newValue: null, added: false }
            ]);

        } else if (newValue === LoginHintSecurityMethodTypes.JWT_ENCRYPTED) {
            propertiesToUpdate.push(...[
                { propertyTypeId: OIDC_ID_TOKEN_USER_ATTRIBUTES_PROPERTY_TYPE_ID, newValue: "true", added: true },
                { propertyTypeId: OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID, newValue: "true", added: true },
                { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID, newValue: "", added: true },
                { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID, newValue: "", added: true }
            ]);
        }

        updateProperties(propertiesToUpdate);
    };
    return (
        <LoginHintContainer
            isDisabled={isDisabled}
            validationErrors={validationErrors}
            documentationLinks={documentationLinks}
            appliedProfiles={workflowDetailsConfigurationState.appliedProfiles}
            properties={[]}
            pageLevel={PageLevelTypes.WORKFLOW}
            workflowId={workflowDetailsConfigurationState.id}
            canModifyNonRestrictedProperties={workflowDetailsConfigurationState.permissions.canModifyNonRestrictedProperties}
            onChangeEnableLoginHint={onChangeEnableLoginHint}
            onChangeLoginHintSecurityMethod={onChangeLoginHintSecurityMethod}
            updateProperties={updateProperties}
        />
    );
};